import { ClientRegistry } from '../components';
import {
  ClientFilteredComponentSystem,
  FilteredClientComponentSystemUpdateEntityArgs,
} from './ClientBase';

/**
 * System that ensures spritesheets are loaded for entities with SpritesheetSprite.
 * Triggers dynamic loading of required spritesheets based on SpritesheetSprite.spritesheetId.
 */
export class SpriteLoadingSystem extends ClientFilteredComponentSystem {
  public componentClasses: (keyof ClientRegistry)[] = ['SpritesheetSprite'];

  // Cache of spritesheet load requests to avoid duplicate requests
  private requestedSpritesheets = new Set<string>();

  run(deltaTime: number): void {
    // Check for hot-reload events once per tick to clear internal request tracking.
    // This allows spritesheets to be re-requested even if they were already marked as requested.
    const hotReloadEvents = this.clientServices.common.eventBus.getEventsOfType('HotReload');
    if (hotReloadEvents.length > 0) {
      this.clearCache();
    }

    super.run(deltaTime);
  }

  updateEntity({ world, entityId }: FilteredClientComponentSystemUpdateEntityArgs): void {
    const spritesheetSprite = world.getComponent(entityId, 'SpritesheetSprite')!;
    const spritesheetService = this.clientServices.spritesheet;
    const spritesheetId = spritesheetSprite.spritesheetId;

    // Already loaded
    if (spritesheetService.isLoaded(spritesheetId)) {
      return;
    }

    // Already failed
    if (spritesheetService.hasFailed(spritesheetId)) {
      return;
    }

    // Request load if not already requested
    if (!this.requestedSpritesheets.has(spritesheetId)) {
      this.requestedSpritesheets.add(spritesheetId);
      spritesheetService.loadSpritesheet(spritesheetId).catch((err) => {
        console.error(`[SpriteLoadingSystem] Failed to load spritesheet '${spritesheetId}':`, err);
      });
    }
  }

  /**
   * Clears the set of requested spritesheets, allowing them to be re-requested
   * (useful during hot-reloads).
   */
  public clearCache(): void {
    this.requestedSpritesheets.clear();
  }
}
